import React from 'react';
import { Loader2 } from 'lucide-react';
import { useLanguage } from '../../i18n/index.jsx';

// Email shape gate for the Universal Search Bar: a simple x@y.z test decides
// whether a query triggers directory DISCOVERY (this card) vs the word-picture
// row filter (FE-6). Deliberately permissive — the BE `discover` endpoint is the
// real validator and returns a `state` discriminator.
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// The circular "PAI Bulb": white circle / black "PAI" text (rail-logo convention),
// distinct from a contact's initials ball so a discovered stranger never looks like
// a saved contact.
const PaiBulb = () => (
    <div
        data-testid="ghost-pai-bulb"
        className="w-10 h-10 rounded-full bg-white border border-pai text-black flex items-center justify-center text-[11px] font-bold flex-shrink-0"
    >
        PAI
    </div>
);

// Presentational discovery card driven entirely by the Hub's `discovery` state.
// discovery = { state, email, requestId?, contactId?, error? }.
const GhostCard = ({ discovery, busy, onSend, onCancel, onAccept, onDecline, onGoToLinked }) => {
    const { t } = useLanguage();
    if (!discovery) return null;
    const { state, email, error } = discovery;

    const errorLine = error ? (
        <div data-testid="ghost-error" className="mt-2 text-xs text-pai-red">{error}</div>
    ) : null;

    if (state === 'self') {
        return (
            <div data-testid="ghost-self" className="bg-white/[0.03] rounded-row border border-pai-subtle p-3 text-sm text-pai-secondary">
                {t('ghostSelf')}
            </div>
        );
    }

    if (state === 'not_found') {
        return (
            <div data-testid="ghost-not-found" className="bg-white/[0.03] rounded-row border border-pai-subtle p-3 text-sm text-pai-meta">
                {t('ghostNotFound')}
            </div>
        );
    }

    // found / pending_sent / pending_received / linked → the PAI-bulb card.
    return (
        <div data-testid="ghost-card" className="bg-white/[0.03] rounded-row border border-pai-subtle p-3">
            <div className="flex items-center gap-3">
                <PaiBulb />
                <div className="min-w-0 flex-1">
                    <div data-testid="ghost-email" className="text-sm font-medium text-white truncate">{email}</div>
                    <div className="text-[11px] text-pai-meta">{t('ghostPaiUser')}</div>
                </div>
                {busy && <Loader2 size={16} className="text-pai-faint animate-spin flex-shrink-0" />}
            </div>

            <div className="mt-3">
                {state === 'found' && (
                    <button
                        data-testid="ghost-send-request"
                        onClick={onSend}
                        disabled={busy}
                        className="pai-btn-primary w-full"
                    >
                        {t('ghostSendRequest')}
                    </button>
                )}

                {state === 'pending_sent' && (
                    <div className="space-y-1">
                        <button
                            data-testid="ghost-request-sent"
                            disabled
                            className="w-full px-3 py-1.5 text-sm font-medium text-pai-meta bg-white/[0.06] rounded-chip cursor-default"
                        >
                            {t('ghostRequestSent')}
                        </button>
                        <button
                            data-testid="ghost-cancel-request"
                            onClick={onCancel}
                            disabled={busy}
                            className="w-full text-[11px] text-pai-red hover:underline"
                        >
                            {t('ghostCancelRequest')}
                        </button>
                    </div>
                )}

                {state === 'pending_received' && (
                    <div className="space-y-2">
                        <p className="text-xs text-pai-secondary">{t('ghostPendingReceived')}</p>
                        <div className="flex gap-2">
                            <button
                                data-testid="ghost-accept"
                                onClick={onAccept}
                                disabled={busy}
                                className="pai-btn-primary flex-1 disabled:opacity-50"
                            >
                                {t('ghostAccept')}
                            </button>
                            <button
                                data-testid="ghost-decline"
                                onClick={onDecline}
                                disabled={busy}
                                className="flex-1 px-3 py-1.5 text-sm font-medium text-pai-secondary bg-white/[0.06] hover:bg-white/[0.09] rounded-chip"
                            >
                                {t('ghostDecline')}
                            </button>
                        </div>
                    </div>
                )}

                {state === 'linked' && (
                    <button
                        data-testid="ghost-linked"
                        onClick={onGoToLinked}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold text-pai-green border border-pai-green/40 bg-pai-green/[0.1] rounded-full hover:bg-pai-green/[0.16] transition-colors"
                    >
                        {t('ghostLinked')}
                    </button>
                )}
            </div>
            {errorLine}
        </div>
    );
};

export default GhostCard;
